
import { useState } from "react";
import { Button } from "@/components/ui/button";
import { FileIcon, FilesIcon, FileArchive, Download } from "lucide-react";
import { toast } from "@/hooks/use-toast";
import { exportToCSV, exportToExcel, formatMembersForExport } from "../../utils/exportData";
import { Membership } from "../../utils/types";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { supabase } from "@/integrations/supabase/client";

interface ExportButtonsProps {
  members: Membership[];
}

export const ExportButtons = ({ members }: ExportButtonsProps) => {
  const [isDownloadingCerfa, setIsDownloadingCerfa] = useState(false);

  const handleExportToExcel = () => {
    if (members.length === 0) {
      toast({ title: "Aucune donnée à exporter", description: "La liste des membres est vide.", variant: "destructive" });
      return;
    }
    try {
      const formattedData = formatMembersForExport(members);
      exportToExcel(formattedData, `membres_export_${new Date().toISOString().split('T')[0]}`);
      toast({ title: "Export réussi", description: "Le fichier Excel a été téléchargé." });
    } catch (error) {
      toast({ title: "Erreur lors de l'export", description: "Impossible d'exporter les données vers Excel.", variant: "destructive" });
    }
  };
  
  const handleExportToCSV = () => {
    if (members.length === 0) {
      toast({ title: "Aucune donnée à exporter", description: "La liste des membres est vide.", variant: "destructive" });
      return;
    }
    try {
      const formattedData = formatMembersForExport(members);
      exportToCSV(formattedData, `membres_export_${new Date().toISOString().split('T')[0]}`);
      toast({ title: "Export réussi", description: "Le fichier CSV a été téléchargé." });
    } catch (error) {
      toast({ title: "Erreur lors de l'export", description: "Impossible d'exporter les données vers CSV.", variant: "destructive" });
    }
  };

  const handleDownloadAllCerfa = async () => {
    const memberIds = members.map(m => m.id);
    if (memberIds.length === 0) {
      toast({ title: "Aucune donnée", description: "La liste des membres filtrés est vide.", variant: "destructive" });
      return;
    }

    try {
      setIsDownloadingCerfa(true);
      toast({ title: "Téléchargement en cours...", description: "Préparation de l'archive ZIP des CERFAs. Cela peut prendre un moment." });

      const { data: { session } } = await supabase.auth.getSession();
      if (!session) {
        toast({ title: "Erreur", description: "Vous devez être connecté.", variant: "destructive" });
        return;
      }

      const response = await fetch(
        `${import.meta.env.VITE_SUPABASE_URL}/functions/v1/download-all-cerfa`,
        {
          method: 'POST',
          headers: {
            Authorization: `Bearer ${session.access_token}`,
            apikey: import.meta.env.VITE_SUPABASE_PUBLISHABLE_KEY,
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({ membershipIds: memberIds }),
        }
      );

      if (!response.ok) {
        const err = await response.json();
        throw new Error(err.error || "Erreur lors du téléchargement");
      }

      const blob = await response.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `CERFA_membres_${new Date().toISOString().split('T')[0]}.zip`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);

      toast({ title: "Téléchargement terminé", description: "L'archive ZIP des CERFAs a été téléchargée." });
    } catch (err: any) {
      console.error("Erreur téléchargement CERFAs:", err);
      toast({ title: "Erreur", description: err.message || "Impossible de télécharger les CERFAs.", variant: "destructive" });
    } finally {
      setIsDownloadingCerfa(false);
    }
  };

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button variant="outline" className="flex items-center gap-2">
          <Download size={16} />
          Exporter
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" className="bg-white">
        <DropdownMenuItem onClick={handleExportToExcel} className="flex items-center gap-2">
          <FileIcon size={16} />
          <span>Excel</span>
        </DropdownMenuItem>
        <DropdownMenuItem onClick={handleExportToCSV} className="flex items-center gap-2">
          <FilesIcon size={16} />
          <span>CSV</span>
        </DropdownMenuItem>
        <DropdownMenuSeparator />
        <DropdownMenuItem
          onClick={handleDownloadAllCerfa}
          disabled={isDownloadingCerfa}
          className="flex items-center gap-2"
        >
          <FileArchive size={16} />
          <span>{isDownloadingCerfa ? "Téléchargement..." : "CERFAs filtrés (ZIP)"}</span>
        </DropdownMenuItem>
      </DropdownMenuContent>
    </DropdownMenu>
  );
};
